import type { DateTime } from 'luxon'
import type {
  ExportScheduleDateChange,
  ExportScheduleDateField,
} from '~/models/exports'
import type { VesselScheduleForExport } from '~/services/apiClient'
import { fastParseISO } from '~/utils'

/**
 * The recent-change fields `GET /exports/` (`VesselScheduleForExport`) and
 * `GET /vessels/etas/` (`GETVesselSchedule`) both carry — same names, same
 * 48h window on the backend.
 *
 * Picked from one payload and checked against the other by the compiler, so
 * the two models parse the fact through this one function and can't resolve
 * it differently: a marker on the List that disagrees with the Schedule board
 * about the same vessel reads as a bug in one of them.
 */
export type ScheduleChangePayload = Pick<
  VesselScheduleForExport,
  | 'changed_at'
  | 'previous_erd'
  | 'previous_cargo_cutoff'
  | 'previous_reefer_cutoff'
  | 'previous_hazmat_cutoff'
>

/** What a schedule model keeps of its recent-change fields. */
export interface ParsedScheduleChanges {
  /**
   * When the schedule's ERD or a cutoff last moved, if that was inside the
   * backend's recent-change window. The frontend applies no window of its own,
   * or the two drift.
   */
  changedAt: DateTime | undefined
  /**
   * Each rendered date's value before its most recent move in that window.
   *
   * `documentation_cutoff` comes down the payload too and is deliberately
   * dropped: no surface shows that date, so a marker for it would flag a date
   * the user cannot see.
   */
  previousDates: Partial<Record<ExportScheduleDateField, DateTime>>
}

export function parseScheduleChanges(
  data: ScheduleChangePayload
): ParsedScheduleChanges {
  return {
    changedAt: data.changed_at ? fastParseISO(data.changed_at) : undefined,
    // A field that was empty and has now been published comes back with a null
    // previous value, indistinguishable here from one that never moved. That is
    // the right outcome either way: first publication is not a date changing
    // under a dispatcher who already planned against it.
    previousDates: {
      erd: data.previous_erd ? fastParseISO(data.previous_erd) : undefined,
      cargo_cutoff: data.previous_cargo_cutoff
        ? fastParseISO(data.previous_cargo_cutoff)
        : undefined,
      reefer_cutoff: data.previous_reefer_cutoff
        ? fastParseISO(data.previous_reefer_cutoff)
        : undefined,
      hazmat_cutoff: data.previous_hazmat_cutoff
        ? fastParseISO(data.previous_hazmat_cutoff)
        : undefined,
    },
  }
}

/**
 * How one date moved recently, or undefined if that one didn't.
 *
 * Per field rather than per schedule: a cutoff that moved says nothing about
 * the ERD, and a column shows only the date its row is actually held to.
 * Flagging off the schedule-level `changedAt` alone would put a marker on a
 * date that hasn't moved.
 */
export function scheduleChangeFor(
  { changedAt, previousDates }: ParsedScheduleChanges,
  field: ExportScheduleDateField
): ExportScheduleDateChange | undefined {
  const previous = previousDates[field]
  if (!previous || !changedAt) return undefined
  return { previous, changedAt }
}
